import { Injectable, DestroyRef, Provider, computed, inject, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { SelectedMerchantService } from './selected-merchant.service';
import { GetTpvUserAccessUseCase } from '@app/domain/use-cases/tpv/get-tpv-user-access.use-case';
import { TpvUserAccess } from '@app/domain/models/tpv/tpv-user-access.model';
import { resolveTpvFocus } from '@app/domain/models/tpv/tpv-entity-focus.model';
import { TpvEntityFocusStore } from '@app/domain/ports/tpv/tpv-entity-focus.store';
import { ExternalCrumb } from '@app/shared/components/combined-breadcrumb/combined-breadcrumb.component';
import { TAB_MEMORIES } from '@app/core/tab-memory/tab-memory.token';

/**
 * Arma los niveles del breadcrumb de TPV. Lógica PROPIA de TPV (separada de SPEI):
 *
 *   Empresa › UdN (si tiene) › Comercio
 *
 * - **Empresa / UdN / Comercio**: salen del ÁRBOL DE ACCESOS de TPV
 *   (`companies → units → merchants`), ubicando el comercio seleccionado por su UUID.
 *   Es la fuente confiable de la jerarquía (la misma que usa el selector del dashboard).
 * - **Proveedor**: NO se muestra. Antes se pintaba el provider de la Cuenta Padre SPEI
 *   activa (p.ej. "ARCUS"), pero eso es contexto de SPEI, NO de TPV (cuyo proveedor de
 *   integración es MEMPHIS) → confundía. La línea TPV se ancla por su propia jerarquía.
 *
 * Cada nivel se pinta SOLO si existe → degrada limpio. Alimenta al componente de
 * presentación compartido vía su `@Input() entityCrumbs` (sin tocar la lógica de SPEI).
 */
@Injectable({ providedIn: 'root' })
export class TpvBreadcrumbService {
  private readonly selectedMerchant = inject(SelectedMerchantService);
  private readonly getUserAccess = inject(GetTpvUserAccessUseCase);
  private readonly destroyRef = inject(DestroyRef);
  // Foco empresa/unidad de `/products` (feature 152, R24). Opcional: sin el
  // enlace del puerto las migas son las de siempre.
  private readonly focusStore = inject(TpvEntityFocusStore, { optional: true });

  /**
   * Migas del FOCO (feature 152, R24) — opt-in EXPLÍCITO. Lo enciende SOLO la pantalla
   * que de verdad acota sus datos por el foco: el dashboard de TPV (su selector de
   * comercio) y, desde 2026-10-16, Transacciones, Dispersiones y Comercios mientras el
   * foco siga APLICADO como filtro Empresa/Unidad (`TpvFocusFilterService`). Las demás
   * pantallas TPV muestran todo el alcance y sus migas no pueden afirmar una
   * empresa/unidad que la tabla no aplica (revisión 1, decisión del líder 2026-10-14).
   * Por bandera y no por ruta: depender de la URL desde un servicio raíz sería frágil.
   */
  private readonly focusCrumbs = signal(false);

  /**
   * Lo llaman el dashboard de TPV (al montarse y al destruirse) y `TpvFocusFilterService`
   * (al aplicar o quitar el filtro del foco, y al destruirse la pantalla).
   */
  showFocusCrumbs(activo: boolean): void {
    this.focusCrumbs.set(activo);
  }

  /** Árbol de accesos (companies→units→merchants), cargado una vez y cacheado. */
  private readonly access = signal<TpvUserAccess | null>(null);
  /**
   * Revisión 2026-10-19 (B1): el árbol es del USUARIO. Se pide la primera vez que se
   * leen las migas (no al construir: `LogoutService` puede crear este servicio antes de
   * haber sesión) y `clear()` lo olvida al cerrar sesión o iniciar un login; la próxima
   * lectura lo vuelve a pedir. Mientras tanto no hay árbol: las migas no pintan nombres
   * del usuario anterior.
   */
  private loadRequested = false;
  private loadSub: Subscription | null = null;
  /** Sube con cada `clear()`: obliga a recalcular aunque el árbol ya fuera `null`. */
  private readonly generation = signal(0);

  constructor() {
    this.destroyRef.onDestroy(() => this.loadSub?.unsubscribe());
  }

  /** Olvida el árbol (memoria de pestaña; lo llama `LogoutService.forgetTabMemory()`). */
  clear(): void {
    this.loadSub?.unsubscribe();
    this.loadSub = null;
    this.loadRequested = false;
    this.access.set(null);
    this.generation.update((n) => n + 1);
  }

  /**
   * Pide el árbol UNA vez. Se llama desde las migas dentro de `untracked`: así la
   * respuesta puede escribir `access` aunque llegue en el mismo ciclo.
   */
  private requestLoad(): void {
    if (this.loadRequested) return;
    this.loadRequested = true;
    this.loadSub = this.getUserAccess.execute().subscribe({
      next: (a) => this.access.set(a),
      error: () => this.access.set(null),
    });
  }

  /** Niveles del breadcrumb TPV. Reactivo al comercio seleccionado y al árbol. */
  readonly entities = computed<ExternalCrumb[]>(() => {
    this.generation();
    untracked(() => this.requestLoad());
    const crumbs: ExternalCrumb[] = [];

    const sel = this.selectedMerchant.selected();
    if (!sel?.id) {
      // R24 (solo con el opt-in): sin comercio pero con foco, las migas dicen la
      // empresa (y la unidad). Se resuelven desde el árbol: el foco no guarda nombres.
      if (!this.focusCrumbs()) return crumbs; // resto de pantallas: como antes de la 152
      const foco = this.focusStore?.focus() ?? null;
      const resuelto = foco ? resolveTpvFocus(this.access(), foco) : null;
      if (!resuelto) return crumbs; // sin comercio ni foco legible: breadcrumb vacío
      const empresaFoco = (resuelto.company.name ?? '').trim();
      const unidadFoco = (resuelto.unit?.name ?? '').toString().trim();
      if (empresaFoco) crumbs.push({ label: empresaFoco, captionKey: 'shell.breadcrumbs.hierarchy.company' });
      if (unidadFoco) crumbs.push({ label: unidadFoco, captionKey: 'shell.breadcrumbs.hierarchy.businessUnit' });
      return crumbs;
    }

    // Ubicar empresa/unidad del comercio caminando el árbol de accesos.
    let empresa = '';
    let unidad = '';
    let comercio = (sel.name ?? '').trim();
    const tree = this.access();
    if (tree) {
      for (const company of tree.companies ?? []) {
        for (const unit of company.units ?? []) {
          const m = (unit.merchants ?? []).find((x) => x.idMerchant === sel.id);
          if (m) {
            empresa = (company.name ?? '').trim();
            unidad = (unit.name ?? '').toString().trim();
            comercio = (m.name ?? '').trim() || comercio;
            break;
          }
        }
        if (empresa) break;
      }
    }

    if (empresa) crumbs.push({ label: empresa, captionKey: 'shell.breadcrumbs.hierarchy.company' });
    if (unidad) crumbs.push({ label: unidad, captionKey: 'shell.breadcrumbs.hierarchy.businessUnit' });
    if (comercio) crumbs.push({ label: comercio, captionKey: 'shell.breadcrumbs.hierarchy.comercio' });
    return crumbs;
  });
}

/**
 * Registra el árbol de las migas como memoria de pestaña (`TAB_MEMORIES`), con
 * `useExisting`: tiene que ser el MISMO singleton que leen las pantallas. Va SOLO en
 * `app.config.ts`.
 */
export function provideTpvBreadcrumbTabMemory(): Provider[] {
  return [{ provide: TAB_MEMORIES, multi: true, useExisting: TpvBreadcrumbService }];
}
